import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import type { UseFormRegister, FieldErrors, FieldValues, Path } from 'react-hook-form';

import RequiredMark from '@/shared/components/ui/RequiredMark';

interface IFormInputProps<T extends FieldValues> {
  name: Path<T>;
  label?: ReactNode;
  labelClassName?: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'date';
  placeholder?: string;
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  showRequiredMark?: boolean;
  /** Renders inside the input row (e.g. password visibility toggle). */
  rightSlot?: ReactNode;
  validation?: {
    required?: string | boolean;
    minLength?: { value: number; message: string };
    maxLength?: { value: number; message: string };
    pattern?: { value: RegExp; message: string };
    validate?: Record<string, (value: string) => string | boolean>;
    dateValidation?: {
      minDate?: string;
      maxDate?: string;
      minDateMessage?: string;
      maxDateMessage?: string;
    };
  };
  icon?: LucideIcon;
  disabled?: boolean;
  className?: string;
  containerClassName?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  preventEmptySpaces?: boolean;
}

const FormInput = <T extends FieldValues>({
  name,
  label,
  labelClassName = '',
  type = 'text',
  placeholder,
  register,
  errors,
  validation,
  icon: Icon,
  rightSlot,
  disabled = false,
  className = '',
  containerClassName = '',
  onChange,
  onKeyDown,
  onFocus,
  preventEmptySpaces = true,
  showRequiredMark = true,
}: IFormInputProps<T>) => {
  const error = errors[name];
  const errorMessage = error?.message as string;

  // Enhanced validation to prevent empty spaces and date validation
  // Skip empty space validation for number, date, and tel input types
  const shouldSkipEmptySpaceValidation = type === 'number' || type === 'date' || type === 'tel';

  const enhancedValidation = {
    ...validation,
    validate: {
      ...validation?.validate,
      noEmptySpaces: (value: string | number) => {
        if (!preventEmptySpaces || shouldSkipEmptySpaceValidation) {
          return true;
        }
        if (value && typeof value === 'string' && value.trim() !== value) {
          return 'Cannot start or end with spaces';
        }
        return true;
      },
      noOnlySpaces: (value: string | number) => {
        if (!preventEmptySpaces || shouldSkipEmptySpaceValidation) {
          return true;
        }
        if (value && typeof value === 'string' && value.trim().length === 0) {
          return 'Cannot contain only spaces';
        }
        return true;
      },
      dateValidation: (value: string) => {
        if (type !== 'date' || !value || !validation?.dateValidation) {
          return true;
        }

        const { minDate, maxDate, minDateMessage, maxDateMessage } = validation.dateValidation;
        const inputDate = new Date(value);

        if (minDate) {
          const minDateObj = new Date(minDate);
          if (inputDate < minDateObj) {
            return minDateMessage || `Date cannot be earlier than ${minDate}`;
          }
        }

        if (maxDate) {
          const maxDateObj = new Date(maxDate);
          if (inputDate > maxDateObj) {
            return maxDateMessage || `Date cannot be later than ${maxDate}`;
          }
        }

        return true;
      },
    },
  };

  const registerProps = register(name, enhancedValidation);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Prevent leading spaces only for text-based inputs
    if (
      preventEmptySpaces &&
      !shouldSkipEmptySpaceValidation &&
      typeof value === 'string' &&
      value.startsWith(' ')
    ) {
      value = value.trimStart();
      e.target.value = value;
    }

    if (onChange) {
      onChange(e);
    } else {
      registerProps.onChange(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Trim whitespace on blur only for text-based inputs
    if (preventEmptySpaces && !shouldSkipEmptySpaceValidation) {
      const trimmedValue = e.target.value.trim();
      if (trimmedValue !== e.target.value) {
        e.target.value = trimmedValue;
        // Trigger onChange to update form state
        const changeEvent = {
          ...e,
          target: { ...e.target, value: trimmedValue },
        } as React.ChangeEvent<HTMLInputElement>;
        registerProps.onChange(changeEvent);
      }
    }
    registerProps.onBlur(e);
  };

  const padRight = rightSlot ? 'pr-12' : 'pr-4';

  return (
    <div className={`${containerClassName}`}>
      {label && (
        <label
          htmlFor={name}
          className={
            labelClassName || 'block text-base font-semibold text-gray-700 dark:text-gray-200 mb-2'
          }
        >
          <span className='inline-flex items-center gap-1'>
            {label}
            {showRequiredMark && !!validation?.required && <RequiredMark />}
          </span>
        </label>
      )}
      <div className='relative'>
        {Icon && (
          <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none'>
            <Icon className='h-5 w-5 text-gray-400' />
          </div>
        )}
        <input
          autoComplete='off'
          id={name}
          type={type}
          ref={registerProps.ref}
          name={registerProps.name}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          className={`
            w-full ${Icon ? 'pl-12' : 'pl-4'} ${padRight} py-2
            border-2 rounded-xl shadow-sm
            focus:outline-none focus:ring-2 focus:ring-theme-500 focus:border-transparent
            transition-all duration-200
            text-base
            ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-200'}
            ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}
            ${className}
          `}
          placeholder={placeholder}
          disabled={disabled}
        />
        {rightSlot && (
          <div className='absolute inset-y-0 right-0 flex items-center pr-3 z-10'>{rightSlot}</div>
        )}
      </div>
      {error && <p className='mt-2 text-sm text-red-600 font-medium'>{errorMessage}</p>}
    </div>
  );
};

export default FormInput;
